import React, { useMemo, useRef } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Customized,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
/** Redux */
import { useDispatch, useSelector } from 'react-redux';
import { extractModalStateFromAnalysis } from '../../../redux/selectors/analyses';
import { setSingleDayLabelAction } from '../../../redux/slices/analyses';
/** Events handler */
import useIncidents from '../hooks/useIncidents';
/** Chart format hook */
import useYAxisFormat from '../hooks/useYAxisFormat';
/** CHART COMPONENTS */
import CustomYAxisTick from '../CustomYAxisTick';
import CustomXAxisTick from '../CustomXAxisTick';
import CustomTooltip from '../CustomTooltip';
/** INTERFACES */
/** UTILS */
import { isRightOrCmdMetaClickMouseDownEvent } from '../utils';
import {
  BAR_CHART_MIN_POINT_SIZE,
  BAR_CHART_STROKE_WIDTH,
  CHART_MARGIN,
  DEFAULT_X_AXIS_CONFIG,
  DEFAULT_Y_AXIS_DOMAIN,
  X_AXIS_PADDING,
} from '../constants/utils';
import {
  useSingleChartDataTooltip,
  usePrepareCohort,
  useChartLines,
  useYAxisWidth,
} from '../hooks';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { FunnelModeKey, QueryType } from '../../../utils/apollo/graphqlSchema';
import isNil from 'lodash/isNil';
import { getFunnelTooltipOverride } from './utils';
import { bootstrapTimezoneSelector } from '../../../redux/selectors/bootstrap';
import { useAppSelector } from '../../../redux/hooks';
import { layout } from '../../../config/styles';
import FunnelTimeToConvertStats from '../FunnelTimeToConvertStats';
import {
  FunnelTimeToConvertStatType,
  getFunnelTimeToConvertStatistics,
  IFunnelTimeToConvertStatistics,
} from '../FunnelTimeToConvertStats/utils';
import { getFunnelDataKeysPerLine, MeasureMode } from './dataKeysUtils';
import { IGNORED_LINE_NAME } from '../hooks/useChartLines';
import { CustomFunnelLabels, generateBarId } from './CustomFunnelLabels';
import { ChartContextMenu, useChartContextMenu } from '../ChartContextMenu';
import { IParsedChartDataRecord, IRange, YAxisPosition } from '../interfaces';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useBuilderInfoDisplay from '../utils/useBuilderInfoDisplay';
import { useOptimizedChartData } from '../../../utils/hooks/useOptimizedChartData';
import { expandedMenuDataSchema } from '../ChartContextMenu/useChartContextMenu';
import { ChartMode } from '../types';
import calculateBarSize from '../utils/calculateBarSize';
import SingleChartDataTooltip from '../SingleChartDataTooltip';
import { useTooltipWheelContext } from '../CustomTooltip/CustomTooltipWheelContext';
import { useBarChartOpacity } from '../hooks/useBarChartOpacity';
import useCacheParsedChartLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheParsedChartLlmRawDataEffect';
import { useFloatingLayerContext } from '../../FloatingManager';
import { freeze } from '@reduxjs/toolkit';
import { IContextualMenuItem } from '@fluentui/react';
import ContextualMenuOption from '../../Dropdown/ContextualMenu/ContextualMenuOption';
import PopperPortal from '../CustomTooltip/PopperPortal';
import { IBreakdownOverallStatus } from '../../../utils/interfaces';
import { useChartContext } from '../ChartContext';
interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const referenceLineXAxisId = 'referenceLineXAxisId';

const renderReferenceLine = (
  xAxisRange: IRange | undefined,
  statistics: IFunnelTimeToConvertStatistics[] | null,
): React.ReactNode[] | null => {
  if (isNil(xAxisRange) || isNil(statistics)) {
    return null;
  }

  // Funnel charts are categorical, we need number ChartXAxis to
  // position the reference line accurately
  return [
    <XAxis
      key="reference-line-x-axis"
      hide
      xAxisId={referenceLineXAxisId}
      padding={X_AXIS_PADDING}
      type="number"
      domain={xAxisRange}
    />,
    ...statistics.map((s, index) => {
      if (!isNil(s.value)) {
        return (
          <ReferenceLine
            key={`reference-line-${s.key}-${index}`}
            xAxisId={referenceLineXAxisId}
            x={s.value}
            strokeWidth={1}
            strokeDasharray={
              s.type === FunnelTimeToConvertStatType.AVERAGE ? '4 2' : undefined
            }
            stroke={s.lineColor}
          />
        );
      } else {
        return null;
      }
    }),
  ];
};

export default React.memo(({ analysisId, mode }: IProps) => {
  const dispatch = useDispatch();
  const { tooltipContentRef } = useTooltipWheelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const modalsStateMemoizedPropSelector = useMemo(
    extractModalStateFromAnalysis,
    [],
  );

  const orgTimezone = useAppSelector(bootstrapTimezoneSelector);

  /* Chart modals state */
  const isCreateIncidentModalOpen = useSelector((state) =>
    modalsStateMemoizedPropSelector(
      state,
      analysisId,
      'isCreateIncidentModalOpen',
    ),
  );
  const createIncidentData = useSelector((state) =>
    modalsStateMemoizedPropSelector(state, analysisId, 'createIncidentData'),
  );

  const {
    chartData,
    settings: { chartSettings, chartWidth, incidents },
    metadata: { isBreakdownOverall, query },
    displayedSlices,
    colorConfig,
    displayedMeasures,
  } = useChartContext();
  const { activeIncidentTypes, axisSettings } = chartSettings;

  assertCondition(query?.__typename === 'FunnelQuery');

  const isCompactMode = mode === ChartMode.Compact;

  // narrow down chartData to IParsedChartDataFunnel
  assertCondition(chartData.queryType === QueryType.Funnel);

  const { linesMeta, data, xAxisKey: funnelXAxisKey, xAxisRange } = chartData;
  const { chartType, isFunnelRelativeLabels } = chartSettings;
  const { visibleLines } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });

  const statistics = getFunnelTimeToConvertStatistics(
    visibleLines,
    chartData,
    isBreakdownOverall,
    colorConfig,
  );

  const {
    onBarMouseMove,
    onMouseLeave,
    tooltipProps,
    clearLockedTooltipProps,
    lockTooltipPositionHandler,
    isTooltipLocked,
  } = useSingleChartDataTooltip({
    containerRef,
  });

  // KBT-1311, disable rechart tooltip when no breakdown in funnel Chart
  const enableRechartTooltip =
    isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdown ||
    chartData.mode === FunnelModeKey.Conversion;

  const { setActiveLabel, getBarOpacity } = useBarChartOpacity();

  const { handleCreateCohortFromFunnel } = usePrepareCohort({
    analysisId,
  });
  const chartDates = useMemo(
    () =>
      data.map((singleDataPoint) => singleDataPoint[funnelXAxisKey] as string),
    [data, funnelXAxisKey],
  );

  const { renderIncidents, renderCreateIncidentReferenceLine } = useIncidents({
    analysisId,
    incidents,
    createIncidentData,
    isCreateIncidentModalOpen,
    chartDates,
    activeIncidentTypes,
    yAxisId: 0,
  });

  const { menuTarget, menuData, openChartContextMenu, closeChartContextMenu } =
    useChartContextMenu();

  const allowToExpand =
    chartData.mode === FunnelModeKey.Conversion && !isCompactMode;
  const handleExpand = () => {
    const result = expandedMenuDataSchema.safeParse(menuData);
    if (result.success) {
      dispatch(
        setSingleDayLabelAction({
          analysisId,
          singleDayLabel: result.data.activeLabel,
        }),
      );
      closeChartContextMenu();
    }
  };

  const contextualMenuItems = freeze<IContextualMenuItem[]>(
    [
      {
        key: 'expand-bar',
        onRenderContent: () => (
          <ContextualMenuOption label={'Expand'} onClick={handleExpand} />
        ),
      },
    ],
    true,
  );

  const singleDayTrigger = (rechartEvent: any, mouseDownEvent: any) => {
    // if not right click, or not CMD + left click or Ctrl + left click, return.
    if (!isRightOrCmdMetaClickMouseDownEvent(mouseDownEvent)) {
      return;
    }

    openChartContextMenu(mouseDownEvent, rechartEvent);
  };

  const currentFormat = useYAxisFormat({
    forcePercent: chartSettings.isConversionView,
  });

  const dataKeysPerLine = getFunnelDataKeysPerLine(
    chartData,
    chartSettings,
    visibleLines,
    MeasureMode.VISIBLE,
  );

  const funnelTooltipOverride = getFunnelTooltipOverride(
    chartData,
    dataKeysPerLine,
  );
  assertNotNil(funnelTooltipOverride.tooltipContext);

  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);

  const { xAxisSeries, yAxisDataKeyCreator, xAxisKey } = useOptimizedChartData(
    data,
    funnelXAxisKey,
  );

  const numItems = data.length;
  const barSize = calculateBarSize(numItems, chartWidth);

  const tooltipZIndex = useFloatingLayerContext();

  // XXX also cache the raw chart numbers in redux, to be used as a context sent to the LLM
  useCacheParsedChartLlmRawDataEffect({
    chartTypeName: 'Funnel',
    analysisId,
    parsedChartDataRecords: data,
  });

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth();

  return (
    <>
      <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
        <ResponsiveContainer ref={containerRef}>
          <BarChart
            data={xAxisSeries}
            margin={CHART_MARGIN}
            barCategoryGap={'0'}
            // rechart will render differently if barGap is 0, some bar is 0px, but some is 1.
            // therefore use 1 for the consistency display
            barGap={chartSettings.isFunnelClusterMode ? 1 : undefined}
            onMouseDown={allowToExpand ? singleDayTrigger : undefined}
            onMouseMove={(props) => {
              setActiveLabel(props.activeLabel ?? null);
            }}
            onMouseLeave={() => {
              setActiveLabel(null);
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey={xAxisKey}
              {...DEFAULT_X_AXIS_CONFIG}
              tick={({ ...props }) => (
                <CustomXAxisTick
                  {...props}
                  limit={barSize}
                  shouldRotate={false}
                />
              )}
            />
            <YAxis
              padding={{ top: 16, bottom: 0 }}
              hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
              tickSize={0}
              domain={DEFAULT_Y_AXIS_DOMAIN}
              axisLine={false} // Whether to display the Y axis vertical line or not
              tick={
                <CustomYAxisTick
                  measureDisplayFormat={currentFormat}
                  forcePercent={chartSettings.isConversionView}
                />
              }
              tickCount={5}
              tickMargin={10}
              width={yAxisWidth}
            />
            <Tooltip
              cursor={false}
              offset={20}
              wrapperStyle={{
                zIndex: tooltipZIndex,
                position: mode === ChartMode.Compact ? 'fixed' : 'absolute',
              }}
              allowEscapeViewBox={{ x: false, y: true }}
              content={(props: TooltipProps<number, string>) => {
                return isNil(tooltipProps) && enableRechartTooltip ? (
                  <PopperPortal active={props.active}>
                    {CustomTooltip({
                      props,
                      lines: visibleLines,
                      chartType,
                      chartValues: data,
                      linesMeta,
                      timezone: orgTimezone,
                      forceBothValues: true,
                      xAxisKey,
                      displayedMeasures,
                      isFunnelRelativeLabels,
                      displayedSlices,
                      ...funnelTooltipOverride,
                      additionalLabel: builderInfoDisplay,
                      colorConfig,
                      isBreakdownOverall,
                      tooltipPayloadContentRef: tooltipContentRef,
                    })}
                  </PopperPortal>
                ) : null;
              }}
            />
            {renderIncidents}
            {visibleLines.flatMap((line) => {
              const { isConversionView } = chartSettings;
              const dataKeys = dataKeysPerLine[line.name];
              const dataKey: DataKey<IParsedChartDataRecord> | undefined =
                isConversionView ? dataKeys?.percentKey : dataKeys?.valueKey;

              // in case of single date, all slices might be disabled thus we don't have any data
              if (isNil(dataKey)) {
                return null;
              }

              const bounceDataKey: DataKey<IParsedChartDataRecord> | undefined =
                isConversionView
                  ? isFunnelRelativeLabels
                    ? dataKeys?.relativeScaledBouncePercentKey
                    : dataKeys?.bouncePercentKey
                  : isFunnelRelativeLabels
                    ? dataKeys?.relativeScaledBounceKey
                    : dataKeys?.bounceValueKey;

              // in case of single date, all slices might be disabled thus we don't have any data
              if (isNil(dataKey)) {
                return null;
              }

              return [
                <Bar
                  isAnimationActive={false}
                  key={line.name}
                  id={generateBarId(line.name, false)}
                  name={line.name}
                  dataKey={yAxisDataKeyCreator(dataKey)}
                  fill={line.color}
                  stackId={line.name}
                  minPointSize={BAR_CHART_MIN_POINT_SIZE}
                  strokeWidth={BAR_CHART_STROKE_WIDTH}
                  barSize={barSize}
                  onMouseMove={(eventProps, xAxisLabelIndex, mouseElement) => {
                    onBarMouseMove({
                      rechartProps: eventProps,
                      line,
                      mouseEvent: mouseElement,
                      xAxisLabelIndex,
                    });
                  }}
                  onMouseLeave={onMouseLeave}
                  onClick={lockTooltipPositionHandler}
                >
                  {data.map((entry, index) => {
                    const opacity = getBarOpacity({
                      tooltipProps,
                      xAxisValue: entry[xAxisKey] as string,
                      lineName: line.name,
                      isTooltipLocked,
                      disableRechartTooltip: !enableRechartTooltip,
                    });
                    return <Cell key={`cell-${index}`} opacity={opacity} />;
                  })}
                </Bar>,
                bounceDataKey ? (
                  <Bar
                    id={generateBarId(line.name, true)}
                    isAnimationActive={false}
                    key={line.name + '-bounce'}
                    name={IGNORED_LINE_NAME}
                    dataKey={yAxisDataKeyCreator(bounceDataKey)}
                    fill={line.color}
                    opacity={0.12}
                    stackId={line.name}
                    minPointSize={BAR_CHART_MIN_POINT_SIZE}
                    strokeWidth={BAR_CHART_STROKE_WIDTH}
                    barSize={barSize}
                    onMouseMove={(eventProps, _, mouseElement) => {
                      onBarMouseMove({
                        rechartProps: eventProps,
                        line,
                        mouseEvent: mouseElement,
                      });
                    }}
                    onMouseLeave={onMouseLeave}
                    onClick={lockTooltipPositionHandler}
                  />
                ) : null,
              ];
            })}
            {renderReferenceLine(xAxisRange, statistics)}
            <Customized
              key="labels"
              component={(props) => (
                <CustomFunnelLabels
                  rechartsProps={props}
                  data={data}
                  dataKeysPerLine={dataKeysPerLine}
                  chartSettings={chartSettings}
                />
              )}
            />
            {renderCreateIncidentReferenceLine}
          </BarChart>
        </ResponsiveContainer>
      </ResponsiveChartWrapper>
      {tooltipProps && (
        <SingleChartDataTooltip
          analysisId={analysisId}
          handleCreateCohort={handleCreateCohortFromFunnel}
          tooltipProps={tooltipProps}
          isTooltipLocked={isTooltipLocked}
          clearLockedTooltipProps={clearLockedTooltipProps}
          funnelTooltipContext={funnelTooltipOverride.tooltipContext}
        />
      )}
      {!isCompactMode && !isNil(statistics) && (
        <FunnelTimeToConvertStats
          statistics={statistics}
          isCompactMode={chartWidth <= layout.sliceContainerMinWidth}
        />
      )}
      {allowToExpand && menuTarget && (
        <ChartContextMenu
          items={contextualMenuItems}
          menuTarget={menuTarget}
          onClose={closeChartContextMenu}
        />
      )}
    </>
  );
});
